import type { AllISO2Codes } from "@/utils/lang/iso-map"
import { env } from "@/lib/env"
import { useAuthStore } from "@/stores/auth-store"
import { getCountryISO3 } from "@/utils/lang/iso-convert"

/**
 * HTTP request methods
 * Defines the supported HTTP methods for API requests.
 */
export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH"

/**
 * Configuration for HTTP requests
 * This type extends the standard `RequestInit` interface and includes additional
 * properties for query parameters, base URL, and timeout settings.
 */
export type HttpConfig = {
  /**
   * Query parameters to append to the request URL.
   */
  params?: Record<string, string | number | boolean | undefined | null>

  /**
   * Base URL to override the default base URL of the HTTP client.
   */
  baseUrl?: string

  /**
   * Timeout duration in milliseconds for the request.
   */
  timeout?: number
  responseType?: "json" | "blob" | "text"
} & RequestInit

/**
 * API endpoints configuration
 * Represents the structure of API endpoint mappings.
 */
export type ApiEndpoints = {
  /**
   * The main API endpoint.
   */
  main: string

  /**
   * Additional service-specific endpoints.
   */
  [key: string]: string
}

/**
 * Error response from API
 * Represents the structure of an error response returned by the API.
 */
export type ApiErrorResponse = {
  /**
   * HTTP status code of the error.
   */
  status: number

  /**
   * Error message describing the issue.
   */
  message: string

  /**
   * Optional detailed errors for specific fields or parameters.
   */
  errors?: Record<string, string[]>
}

/**
 * Custom error class for API errors
 * Extends the standard `Error` class to include additional properties
 * for HTTP status and detailed error information.
 */
export class ApiError extends Error {
  /**
   * HTTP status code of the error.
   */
  status: number

  /**
   * Optional detailed errors for specific fields or parameters.
   */
  errors?: Record<string, string[]>

  /**
   * Create a new instance of `ApiError`.
   * @param message - Error message describing the issue.
   * @param status - HTTP status code of the error.
   * @param errors - Optional detailed errors for specific fields or parameters.
   */
  constructor(message: string, status: number, errors?: Record<string, string[]>) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.errors = errors
  }
}

/**
 * Get API endpoints from environment variables
 * Retrieves the API endpoint configuration from environment variables.
 * Defaults to `http://localhost:3001/api` if no environment variable is set.
 * @returns An object containing the API endpoints.
 */
function getApiEndpoints(): ApiEndpoints {
  const main = env.NEXT_PUBLIC_API_URL || "http://localhost:3020/api"
  return {
    main,
    venues: `${main}/venues`,
    competition: `${main}/competitions`,
    results: `${main}/result`,
    sportEvents: `${main}/sport-events`,
  }
}

/**
 * HTTP client for making API requests
 * Provides methods for making HTTP requests (GET, POST, PUT, PATCH, DELETE)
 * with support for custom configurations, query parameters, and error handling.
 */
export class HttpClient {
  /**
   * Base URL for the HTTP client.
   */
  private baseUrl: string

  /**
   * Default configuration for HTTP requests.
   */
  private defaultConfig: HttpConfig

  /**
   * API endpoints configuration.
   */
  private endpoints: ApiEndpoints

  /**
   * Create a new HTTP client
   * @param service - The service to use (e.g., 'main').
   * @param defaultConfig - Default configuration for requests.
   */
  constructor(service: keyof ApiEndpoints = "main", defaultConfig: HttpConfig = {}) {
    this.endpoints = getApiEndpoints()
    this.baseUrl = this.endpoints[service] || this.endpoints.main
    this.defaultConfig = {
      headers: {
        "Content-Type": "application/json",
        "app-id": "ovr",
        // Here you can add any other default headers you want to include in every request
        // "Authorization": `Bearer ${token}`,
      },
      timeout: 30000, // 30 seconds default timeout
      ...defaultConfig,
    }
  }

  /**
   * Get the current locale from the browser cookie (NEXT_LOCALE).
   * Falls back to 'en' if not found or in non-browser environment.
   */
  private getCurrentLocale(): string {
    if (typeof document === "undefined")
      return env.NEXT_PUBLIC_DEFAULT_LOCALE ?? "en"
    const cookies = document.cookie.split(";")
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=")
      if (name === "NEXT_LOCALE") {
        return value || "en"
      }
    }
    return env.NEXT_PUBLIC_DEFAULT_LOCALE ?? "en"
  }

  private deleteCookie(name: string) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  }

  /**
   * Build URL with query parameters
   * Constructs a complete URL by appending query parameters to the base URL or endpoint.
   * @param endpoint - The API endpoint or full URL.
   * @param config - Optional configuration containing query parameters.
   * @returns The constructed URL as a string.
   */
  private buildUrl(endpoint: string, config?: HttpConfig): string {
    const url = new URL(
      endpoint.startsWith("http")
        ? endpoint
        : `${config?.baseUrl || this.baseUrl}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`,
    )

    if (config?.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value))
        }
      })
    }
    return url.toString()
  }

  /**
   * Make an HTTP request
   * Sends an HTTP request to the specified endpoint using the given method and configuration.
   * @template T - The expected response type.
   * @param endpoint - The API endpoint to send the request to.
   * @param method - The HTTP method to use (e.g., 'GET', 'POST').
   * @param data - Optional payload for the request body.
   * @param config - Optional configuration for the HTTP request.
   * @returns A promise that resolves to the response data of type T.
   */
  private async request<T>(endpoint: string, method: HttpMethod, data?: unknown, config?: HttpConfig): Promise<T> {
    const url = this.buildUrl(endpoint, config)
    // Obtener el idioma actual desde la cookie
    const currentLocale = this.getCurrentLocale()
    const parsedLocale = getCountryISO3(currentLocale as AllISO2Codes)
    const token = useAuthStore.getState().user?.accessToken
    const mergedConfig: RequestInit = {
      ...this.defaultConfig,
      ...config,
      method,
      headers: {
        ...this.defaultConfig.headers,
        ...config?.headers,
        "language": parsedLocale,
        "Accept-Language": parsedLocale, // Agrega el idioma desde la cookie NEXT_LOCALE
        "Authorization": `Bearer ${token}`,
      },
    }

    if (data) {
      if (method !== "GET") {
        // Handle FormData specially - don't JSON.stringify and don't set Content-Type
        if (data instanceof FormData) {
          mergedConfig.body = data
          // Remove Content-Type header to let browser set multipart/form-data automatically
          if (mergedConfig.headers && typeof mergedConfig.headers === "object" && !Array.isArray(mergedConfig.headers)) {
            const headers = mergedConfig.headers as Record<string, string>
            delete headers["Content-Type"]
          }
        }
        else {
          mergedConfig.body = JSON.stringify(data)
        }
      }
    }

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), config?.timeout || this.defaultConfig.timeout)

      mergedConfig.signal = controller.signal

      const response = await fetch(url, mergedConfig)
      clearTimeout(timeoutId)

      // Handle non-JSON responses
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        const responseData = await response.json() as ApiErrorResponse

        if (!response.ok) {
          if (response.status === 401 && !url.includes("login")) {
            console.log("Unauthorized")
            await useAuthStore.getState().logout()
          }
          if (response.status === 404) {
            console.log("Resource not found")
            return [] as T
          }
          console.log("Error", responseData)
          return [] as T // instead of throwing an error, return an empty array
        }

        return responseData as T
      }
      else {
        if (!response.ok) {
          if (response.status === 401) {
            console.log("Unauthorized")
            await useAuthStore.getState().logout()
          }
          if (response.status === 404) {
            console.log("Resource not found")
            return [] as T
          }
          const text = await response.text()
          // throw new ApiError(text || `HTTP error! status: ${response.status}`, response.status)
          console.log("Error", text || response)
          return [] as T // instead of throwing an error, return an empty array
        }

        // Para respuestas blob, devolver tanto los datos como los headers
        if (config?.responseType === "blob") {
          const blob = await response.blob()
          const headers: Record<string, string> = {}
          response.headers.forEach((value, key) => {
            headers[key] = value
          })
          return { data: blob, headers } as unknown as T
        }

        // For non-JSON responses like text, etc.
        if (config?.headers instanceof Headers && config.headers.get("Accept") === "application/octet-stream") {
          return (await response.blob()) as unknown as T
        }

        return (await response.text()) as unknown as T
      }
    }
    catch (error) {
      console.log("Error", error)
      if (error instanceof ApiError) {
        console.log("ApiError", error)
        return [] as T
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        console.log("AbortError", error)
        return [] as T
      }

      console.log("Error", error)
      return [] as T
    }
  }

  /**
   * Make a GET request to the specified endpoint.
   * @template T - The expected response type.
   * @param endpoint - The API endpoint to send the GET request to.
   * @param config - Optional configuration for the HTTP request, such as headers or query parameters.
   * @returns A promise that resolves to the response data of type T.
   */
  public async get<T>(endpoint: string, config?: HttpConfig): Promise<T> {
    return this.request<T>(endpoint, "GET", undefined, config)
  }

  /**
   * Make a POST request to the specified endpoint.
   * @template T - The expected response type.
   * @param endpoint - The API endpoint to send the POST request to.
   * @param data - The payload to include in the POST request body.
   * @param config - Optional configuration for the HTTP request, such as headers or query parameters.
   * @returns A promise that resolves to the response data of type T.
   */
  public async post<T>(endpoint: string, data?: unknown, config?: HttpConfig): Promise<T> {
    return this.request<T>(endpoint, "POST", data, config)
  }

  /**
   * Make a PUT request to the specified endpoint.
   * @template T - The expected response type.
   * @param endpoint - The API endpoint to send the PUT request to.
   * @param data - The payload to include in the PUT request body.
   * @param config - Optional configuration for the HTTP request, such as headers or query parameters.
   * @returns A promise that resolves to the response data of type T.
   */
  public async put<T>(endpoint: string, data?: unknown, config?: HttpConfig): Promise<T> {
    return this.request<T>(endpoint, "PUT", data, config)
  }

  /**
   * Make a PATCH request to the specified endpoint.
   * @template T - The expected response type.
   * @param endpoint - The API endpoint to send the PATCH request to.
   * @param data - The payload to include in the PATCH request body.
   * @param config - Optional configuration for the HTTP request, such as headers or query parameters.
   * @returns A promise that resolves to the response data of type T.
   */
  public async patch<T>(endpoint: string, data?: unknown, config?: HttpConfig): Promise<T> {
    return this.request<T>(endpoint, "PATCH", data, config)
  }

  /**
   * Make a DELETE request to the specified endpoint.
   * @template T - The expected response type.
   * @param endpoint - The API endpoint to send the DELETE request to.
   * @param config - Optional configuration for the HTTP request, such as headers or query parameters.
   * @returns A promise that resolves to the response data of type T.
   */
  public async delete<T>(endpoint: string, config?: HttpConfig): Promise<T> {
    return this.request<T>(endpoint, "DELETE", undefined, config)
  }
}

/**
 * Create HTTP clients for different services
 * Pre-configured instances of `HttpClient` for specific services.
 */
export const httpClient = new HttpClient("main")
export const bdmHttpClient = new HttpClient("bdm")
export const boxHttpClient = new HttpClient("box")
export const bk3HttpClient = new HttpClient("bk3")

/**
 * Create a custom HTTP client for a specific service
 * @param service - The service key (e.g., 'main', 'bdm', 'box', 'bk3').
 * @param config - Optional HTTP client configuration.
 * @returns A new instance of `HttpClient` configured for the specified service.
 */
export function createHttpClient(service: keyof ApiEndpoints, config?: HttpConfig): HttpClient {
  return new HttpClient(service, config)
}

export default HttpClient
